import type { CSSProperties, ReactNode } from "react";

export const TimelinePanelSubComponents = {
  HEADER: "TimelinePanelHeader",
  CONTENT: "TimelinePanelContent",
  FOOTER: "TimelinePanelFooter",
} as const;
export type TimelinePanelSubComponentsType =
  (typeof TimelinePanelSubComponents)[keyof typeof TimelinePanelSubComponents];

export type TimelinePanelStoreState = {
  /** Determines whether the TimelinePanel is open. */
  isOpen: boolean;
  /** Sets whether the TimelinePanel is open. */
  setIsOpen: (isOpen: boolean) => void;
  /** Determines the width of the TimelinePanel. */
  drawerWidth: number;
  /** Sets the width of the TimelinePanel. */
  setDrawerWidth: (width: number) => void;
  /** Determines which drawer is opened by TimelinePanel.Trigger. */
  targetDrawer: string | null;
  /** Sets the drawer to open. */
  setTargetDrawer: (source: string | null) => void;
};

export type TimelinePanelTriggerProps = {
  /** The content of the TimelinePanelTrigger. Will overwrite the default button. */
  children?: (
    props: Pick<TimelinePanelStoreState, "isOpen" | "setIsOpen"> & {
      label?: string;
      onClick?: () => void;
    }
  ) => ReactNode;
  /** The label to display within the default button of the TimelinePanelTrigger. */
  label?: string;
  /** Determines which drawer is opened by TimelinePanel.Trigger. */
  targetDrawer: string | null;
};

export type TimelinePanelDrawerProps = {
  /** The content of the TimelinePanelDrawer. */
  children: ReactNode;
  /** The size of the TimelinePanelDrawer, corresponding to widths defined in TimelinePanel.tsx. */
  size?: "sm" | "md" | "lg";
  /** The function executed when the TimelinePanelDrawer is closed. */
  onClose?: () => void;
  /** The classname to apply to the TimelinePanelDrawer. */
  className?: string;
  /** The style to apply to the TimelinePanelDrawer. */
  style?: CSSProperties;
  /** Whether to render a Portal component alongside the TimelinePanel. */
  shouldRenderPortal?: boolean;
  /** A string that identifies the drawer and determines whether it is opened by TimelinePanel.Trigger with a corresponding targetDrawer prop. */
  drawerKey: string | null;
};

export type TimelinePanelHeaderProps = {
  /** The content to render in the header of the TimelinePanelDrawer. */
  title: ReactNode;
  /** The classname to apply to the TimelinePanelHeader. */
  className?: string;
  /** The style to apply to the TimelinePanelHeader. */
  style?: CSSProperties;
};

export type TimelinePanelContentProps = {
  /** The content to render in the content of the TimelinePanelDrawer. */
  children: ReactNode;
  /** Determines whether the loading variant is displayed. Defaults to false. */
  isLoading?: boolean;
  /** The classname to apply to the TimelinePanelContent. */
  className?: string;
  /** The style to apply to the TimelinePanelContent. */
  style?: CSSProperties;
};

export type TimelinePanelFooterProps = {
  /** The content to render in the footer of the TimelinePanelDrawer. */
  children: ReactNode;
  /** The classname to apply to the TimelinePanelFooter. */
  className?: string;
  /** The style to apply to the TimelinePanelFooter. */
  style?: CSSProperties;
};
